import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { useWSEvent } from "@/messaging/WSContext";
import { playMessageSound } from "@/messaging/sound";
import type { Conversation, PresenceStatus, UserBrief } from "@/messaging/types";

/** Sort: pinned first, then most-recent activity. */
function sortConvs(list: Conversation[]): Conversation[] {
  return [...list].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    const ta = a.last_message?.created_at ?? a.created_at;
    const tb = b.last_message?.created_at ?? b.created_at;
    return new Date(tb).getTime() - new Date(ta).getTime();
  });
}

type ListState = "loading" | "ready" | "error";

interface ChatContextValue {
  // shell state
  open: boolean;
  expanded: boolean;
  activeId: string | null;
  openChat: (conversationId?: string) => void;
  close: () => void;
  toggleExpanded: () => void;
  setActive: (id: string | null) => void;
  // data
  conversations: Conversation[];
  listState: ListState;
  unread: number;
  refreshList: () => Promise<void>;
  upsertConversation: (c: Conversation) => void;
  patchConversation: (id: string, partial: Partial<Conversation>) => void;
  removeConversation: (id: string) => void;
  markRead: (id: string) => void;
  // presence
  presenceOf: (u: UserBrief | null | undefined) => PresenceStatus;
}

const Ctx = createContext<ChatContextValue | null>(null);

export function ChatProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const myId = user?.id ?? null;

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      return localStorage.getItem("agencygrid.chat.expanded") === "1";
    } catch {
      return false;
    }
  });
  const [activeId, setActiveId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [listState, setListState] = useState<ListState>("loading");
  // The full list is loaded lazily — only when the drawer first opens. On a
  // normal page we fetch nothing but the cheap unread-count aggregate.
  const [listLoaded, setListLoaded] = useState(false);
  const [unread, setUnread] = useState(0);

  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;
  const listLoadedRef = useRef(listLoaded);
  listLoadedRef.current = listLoaded;
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;
  const openRef = useRef(open);
  openRef.current = open;

  // Live presence overrides (userId → status) from `presence.changed`,
  // layered over the snapshot each `UserBrief` carries.
  const [presence, setPresence] = useState<Record<string, PresenceStatus>>({});

  const fetchUnread = useCallback(async () => {
    try {
      const r = await api<{ unread: number }>("/api/conversations/unread-count");
      setUnread(r.unread);
    } catch {
      /* keep the prior value */
    }
  }, []);

  const refreshList = useCallback(async () => {
    try {
      const r = await api<{ items: Conversation[] }>("/api/conversations");
      setConversations(sortConvs(r.items));
      setListState("ready");
      setListLoaded(true);
    } catch {
      setListState((s) => (s === "ready" ? s : "error"));
    }
  }, []);

  // On every page we need ONLY the unread badge — one cheap aggregate query.
  useEffect(() => {
    if (!user) return;
    void fetchUnread();
  }, [user, fetchUnread]);

  // Load the full conversation list lazily, the first time the drawer opens.
  useEffect(() => {
    if (open && !listLoaded) {
      setListState("loading");
      void refreshList();
    }
  }, [open, listLoaded, refreshList]);

  const upsertConversation = useCallback((c: Conversation) => {
    setConversations((prev) => {
      const idx = prev.findIndex((x) => x.id === c.id);
      const next = idx >= 0 ? prev.map((x) => (x.id === c.id ? c : x)) : [c, ...prev];
      return sortConvs(next);
    });
  }, []);

  const patchConversation = useCallback(
    (id: string, partial: Partial<Conversation>) => {
      setConversations((prev) =>
        sortConvs(prev.map((c) => (c.id === id ? { ...c, ...partial } : c))),
      );
    },
    [],
  );

  const removeConversation = useCallback((id: string) => {
    setConversations((prev) => prev.filter((c) => c.id !== id));
    setActiveId((cur) => (cur === id ? null : cur));
  }, []);

  const markRead = useCallback(
    (id: string) => {
      setConversations((prev) =>
        prev.map((c) => (c.id === id ? { ...c, unread_count: 0 } : c)),
      );
      void fetchUnread();
    },
    [fetchUnread],
  );

  const openChat = useCallback((conversationId?: string) => {
    if (conversationId) setActiveId(conversationId);
    setOpen(true);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  const toggleExpanded = useCallback(() => {
    setExpanded((v) => {
      const next = !v;
      try {
        localStorage.setItem("agencygrid.chat.expanded", next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const setActive = useCallback((id: string | null) => setActiveId(id), []);

  // ── live WS wiring ──────────────────────────────────────────────────────
  useWSEvent("message.new", (ev) => {
    const convId = ev.conversation_id;
    const isMine = ev.message.sender?.id === myId;
    const isActiveOpen = openRef.current && activeIdRef.current === convId;
    // Only touch the list when it's actually loaded (drawer has been opened).
    if (listLoadedRef.current) {
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c.id === convId);
        if (idx < 0) {
          void refreshList();
          return prev;
        }
        const c = prev[idx];
        const bumpUnread = !isMine && !isActiveOpen && !c.muted;
        const updated: Conversation = {
          ...c,
          last_message: {
            id: ev.message.id,
            sender_id: ev.message.sender?.id ?? null,
            sender_name: ev.message.sender?.full_name ?? ev.message.sender?.email ?? null,
            body: ev.message.body,
            has_attachment: !!ev.message.attachment_url,
            deleted: !!ev.message.deleted_at,
            system_kind: ev.message.system_kind ?? null,
            created_at: ev.message.created_at,
          },
          unread_count: bumpUnread ? c.unread_count + 1 : c.unread_count,
        };
        return sortConvs(prev.map((x) => (x.id === convId ? updated : x)));
      });
    }
    if (!isMine && !isActiveOpen) {
      const muted =
        conversationsRef.current.find((c) => c.id === convId)?.muted === true;
      if (!muted) {
        // Bump the badge OPTIMISTICALLY so it appears the instant the message
        // lands — alongside the sound — instead of waiting on (or depending
        // solely on) the async count fetch, which could be slow, fail, or race
        // a read and leave "sound but no badge".
        setUnread((u) => u + 1);
        playMessageSound();
      }
      // Reconcile to the exact server-side count (respects muted / other tabs).
      void fetchUnread();
    }
  });

  useWSEvent("conversation.read", (ev) => {
    // Advance the reader's cursor in the local list so "seen" ticks + the
    // seen-by avatars update live when the OTHER side reads my message.
    setConversations((prev) =>
      prev.map((c) =>
        c.id === ev.conversation_id
          ? {
              ...c,
              members: c.members.map((m) =>
                m.user.id === ev.user_id
                  ? { ...m, last_read_at: ev.last_read_at }
                  : m,
              ),
            }
          : c,
      ),
    );
    if (ev.user_id === myId) markRead(ev.conversation_id);
  });
  useWSEvent("conversation.updated", () => void refreshList());
  useWSEvent("conversation.member.added", () => void refreshList());
  useWSEvent("conversation.member.removed", (ev) => {
    if (ev.user_id === myId && ev.self_left === false) {
      // removed by an admin — drop it locally
      removeConversation(ev.conversation_id);
    } else {
      void refreshList();
    }
  });
  useWSEvent("presence.changed", (ev) => {
    setPresence((prev) => ({ ...prev, [ev.user_id]: ev.status }));
  });

  const presenceOf = useCallback(
    (u: UserBrief | null | undefined): PresenceStatus => {
      if (!u) return "offline";
      const override = presence[u.id];
      if (override) return override;
      if (u.presence_status) return u.presence_status;
      return u.is_online ? "active" : "offline";
    },
    [presence],
  );

  const value = useMemo<ChatContextValue>(
    () => ({
      open,
      expanded,
      activeId,
      openChat,
      close,
      toggleExpanded,
      setActive,
      conversations,
      listState,
      unread,
      refreshList,
      upsertConversation,
      patchConversation,
      removeConversation,
      markRead,
      presenceOf,
    }),
    [
      open,
      expanded,
      activeId,
      openChat,
      close,
      toggleExpanded,
      setActive,
      conversations,
      listState,
      unread,
      refreshList,
      upsertConversation,
      patchConversation,
      removeConversation,
      markRead,
      presenceOf,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useChat(): ChatContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useChat must be used inside <ChatProvider>");
  return v;
}
